import type {EventData, EventName} from '@packages/shared/segment-typewriter';
import type {Event, RUMEvent} from '@packages/utilities/analytics';

type Observer<Context> = (arg1: {
  category: 'error' | 'event' | 'context';
  type: string;
  data?: any;
  context: Context;
}) => Promise<any> | void;
type ConfigDefinition<Context> =
  | Observer<Context>
  | {
      [eventType: string]: Observer<Context>;
    };
type CleanupConfigFn = () => void;

type ProviderModule = {
  default: (...args: Array<any>) => void;
};
type ProviderImporterFn = () => Promise<ProviderModule>;
type LazyLoadProvider = (
  arg1: ProviderImporterFn
) => (...args: Array<any>) => void;

export type TelemetrySendEvent = <K extends EventName>(
  arg1: K,
  data?: EventData<K>
) => void;

export type TelemetrySendDatadogEvent = <K extends `datadog::${string}`>(
  arg1: `datadog::${string}`,
  data?: EventData<K> | RUMEvent
) => void;

export type Telemetry<Context> = {
  setContext: (update: Partial<Context>) => void;
  getContext: () => Context;
  observeAll: (arg1: ConfigDefinition<Context>) => CleanupConfigFn;
  observeContext: (arg1: Observer<Context>) => CleanupConfigFn;
  observeEvents: (arg1: ConfigDefinition<Context>) => CleanupConfigFn;
  observeErrors: (arg1: ConfigDefinition<Context>) => CleanupConfigFn;
  sendEvent: TelemetrySendEvent;
  sendDatadogEvent: TelemetrySendDatadogEvent;
  sendError: (arg1: string, data?: any) => void;
  lazyLoadProvider: LazyLoadProvider;
};

// event type used when an error happens inside the telemetry module,
// for example if third-party code fails and we want to log that
// error somewhere too
const internalError = 'telemetry-error';

// in an ideal world, we could have passed in the Context type as a
// generic here, so the consumer code would look like:
// const telemetry = initTelemetry<Context>()
//
// unfortunately, since the setContext function needs its api to be
// of type ($Shape<Context>) => void, we cannot do this - flow made
// this into an error: https://medium.com/flow-type/flows-improved-handling-of-generic-types-b5909cc5e3c5
//
// instead, we can sacrifice type safety for the context object inside
// the telemetry module, but enforce it in consumer code like this:
// const telemetry: Telemetry<Context> = initTelemetry()
export function initTelemetry(): Telemetry<any> {
  let context: Record<string, any> = {};
  const api: Partial<Telemetry<any>> = {};
  const observers = {
    all: {
      context: new Set(),
      everything: new Set(),
      error: new Set(),
      event: new Set(),
    },
    specific: {
      context: {},
      everything: {},
      error: {},
      event: {},
    },
  } as const;

  // we'll use an idle callback to defer sending any telemetry
  // events until the browser has some spare time
  let isProcessPending = false;
  const queue: Array<{
    category: string;
    data: undefined | Event<EventName> | RUMEvent | any;
    type: string;
  }> = [];

  const isServerSide = typeof window === 'undefined';

  function processQueue() {
    if (isServerSide || !window.requestIdleCallback) {
      // if requestIdleCallback is unavailable, we'll just send
      // the items in the queue synchronously
      while (queue.length > 0) {
        sendNextItem();
      }
    } else if (!isProcessPending && !isServerSide) {
      // if requestIdleCallback is available and is not already
      // pending, we request processing when time is available
      window.requestIdleCallback(sendItemsWhileIdle);
      isProcessPending = true;
    }
  }

  function sendNextItem() {
    // to process each item, we first shift it from the front
    // of the queue
    const {category, type, data} = queue.shift()!;

    // we set up a function to ensure that any errors in
    // third-party modules are caught and routed back
    // into the telemetry system
    const tryToSend = (cb: any) => {
      try {
        cb({category, type, data, context});
      } catch (error: any) {
        if (type !== internalError) {
          // @ts-expect-error - TS2722 - Cannot invoke an object which is possibly 'undefined'.
          api.sendError(internalError, {
            error,
            action: 'sending next item',
          });
        }
      }
    };

    // then we send the event to each observer:
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly context: {}; readonly everything: {}; readonly error: {}; readonly event: {}; }'.
    if (observers.specific[category][type]) {
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly context: {}; readonly everything: {}; readonly error: {}; readonly event: {}; }'.
      observers.specific[category][type].forEach(tryToSend);
    }

    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{}'.
    if (observers.specific.everything[type]) {
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{}'.
      observers.specific.everything[type].forEach(tryToSend);
    }

    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'any' can't be used to index type '{ readonly context: Set<unknown>; readonly everything: Set<unknown>; readonly error: Set<unknown>; readonly event: Set<unknown>; }'.
    observers.all[category].forEach(tryToSend);
    observers.all.everything.forEach(tryToSend);
  }

  function sendItemsWhileIdle(deadline: any) {
    // while we have idle time available, we process each item
    // in the queue individually, until either time runs out
    // or we run out of items in the queue
    while (deadline.timeRemaining() > 0 && queue.length > 0) {
      sendNextItem();
    }
    isProcessPending = false;

    // if we run out of time before the queue is finished, we
    // schedule another callback to continue the queue later
    if (queue.length > 0) {
      processQueue();
    }
  }

  // observe events - can be used to observe a subset of the
  // events which the consumer specifies, or all events
  // returns a cleanup function which unsubscribes
  //
  // type Observer = (Event, Context) => void;
  // type UnsubscribeFn = () => void;
  // type Observe = (Observer | {[event: string]: Observer}) => UnsubscribeFn;
  // type CreateObserve = ('context' | 'everything' | 'events' | 'errors') => Observe
  function createObserveFn(category: string) {
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly context: Set<unknown>; readonly everything: Set<unknown>; readonly error: Set<unknown>; readonly event: Set<unknown>; }'.
    const all = observers.all[category];
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly context: {}; readonly everything: {}; readonly error: {}; readonly event: {}; }'.
    const specific = observers.specific[category];
    // @ts-expect-error - TS7006 - Parameter 'configOrFn' implicitly has an 'any' type.
    return function observe(configOrFn) {
      if (typeof configOrFn === 'function') {
        all.add(configOrFn);

        return () => {
          all.delete(configOrFn);
        };
      }

      const types = Object.keys(configOrFn);

      types.forEach((type) => {
        if (!specific[type]) {
          specific[type] = new Set();
        }
        specific[type].add(configOrFn[type]);
      });

      return () => {
        types.forEach((type) => {
          specific[type].delete(configOrFn[type]);
        });
      };
    };
  }

  // observe both events and errors
  api.observeAll = createObserveFn('everything');
  // observe only events
  api.observeEvents = createObserveFn('event');
  // observe only errors
  api.observeErrors = createObserveFn('error');
  // observe context updates
  api.observeContext = createObserveFn('context');

  // create a send function
  //
  // type Send = ('event' | 'error' | 'context') => (string, data?: any) => void;
  // @ts-expect-error - TS7006 - Parameter 'data' implicitly has an 'any' type.
  const send = (category: string) => (type: string, data) => {
    queue.push({
      category,
      type,
      data,
    });
    processQueue();
  };

  // send an event
  api.sendEvent = send('event');
  // send a datadog event
  api.sendDatadogEvent = send('event');
  // send an error
  api.sendError = send('error');
  // send a context update
  const updateContextObservers = send('context');

  // set contextual information which needs to be shared across
  // multiple events, e.g. user info, route info, etc
  //
  // type SetContext = ($Shape<Context>) => void;
  api.setContext = (contextUpdate: Partial<any>) => {
    updateContextObservers('context-updated', contextUpdate);

    context = {
      ...context,
      ...contextUpdate,
    };
  };

  // get the current context
  api.getContext = () => context;

  const providers = new Map<ProviderImporterFn, ProviderModule>();
  const pendingImports = new Map<ProviderImporterFn, Promise<ProviderModule>>();
  type Args = Array<any>;
  const orphanedEvents = new Map<ProviderImporterFn, Array<Args>>();
  const recordOrphanedEvent = (
    importFn: ProviderImporterFn,
    args: Array<any>
  ) => {
    if (!orphanedEvents.has(importFn)) {
      orphanedEvents.set(importFn, []);
    }
    const arr = orphanedEvents.get(importFn);
    arr && arr.push(args);
  };

  // defer loading of a provider until it's actually needed
  //
  // tyoe ProviderImporterFn = () => Promise<ProviderModule>;
  // type LazyLoadProvider = (ProviderImporterFn) => (...args: Array<any>) => void;
  api.lazyLoadProvider =
    (importFn: ProviderImporterFn) =>
    (...args) => {
      if (providers.has(importFn)) {
        // the provider has already been imported, so we can synchronously
        // call it with the arguments provided
        const provider = providers.get(importFn);
        provider && provider.default(...args);
      } else if (pendingImports.has(importFn)) {
        // the provider import is in progress, so we piggyback on the original
        // promise and call the provider with the args when it resolves
        const promise = pendingImports.get(importFn);
        promise &&
          promise
            .then((provider) => {
              provider.default(...args);
            })
            .catch(() => {
              recordOrphanedEvent(importFn, args);
            });
      } else {
        // no import in progress, so we call the import function, set up the
        // ability to share the promise with subsequent calls, and then call
        // the provider with the args when it resolves
        const promise = importFn();
        pendingImports.set(importFn, promise);
        promise
          .then((provider) => {
            if (orphanedEvents.has(importFn)) {
              const arr = orphanedEvents.get(importFn);
              arr &&
                arr.forEach((orphan) => {
                  provider.default(...orphan);
                });
              orphanedEvents.delete(importFn);
            }
            provider.default(...args);
            providers.set(importFn, provider);
            pendingImports.delete(importFn);
          })
          .catch((error) => {
            recordOrphanedEvent(importFn, args);
            pendingImports.delete(importFn);
            // @ts-expect-error - TS2722 - Cannot invoke an object which is possibly 'undefined'.
            api.sendError(internalError, {
              error,
              action: 'loading lazy provider',
            });
          });
      }
    };

  return api as Telemetry<any>;
}
